import _extends from "@babel/runtime/helpers/extends";
import Calendar from "@mui/icons-material/Event";
import InputAdornment from "@mui/material/InputAdornment";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker as MUIDateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { StaticTimePicker } from "@mui/x-date-pickers/StaticTimePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import classNames from "classnames";
import React, { useEffect, useRef } from "react";
import TextInput from "../TextInput";
import { textInputStyles } from "../TextInput/styles";
import { parseDate } from "../Utilities/DateHelper";
import { makeStyles, useTheme } from "../Utilities/ThemeHelper";
import { CustomDatePickerToolbar, CustomDateTimePickerToolbar, CustomTimePickerToolbar } from "./CustomPickerToolbars";
export const DEFAULT_FORMATS = {
  time: "hh:mm aa",
  dateTime: "MM/dd/yyyy hh:mm aa",
  date: "MM/dd/yyyy",
  dateStatic: "MM/dd/yyyy",
  // Assuming dateStatic uses the same format as date
  dateMobile: "MM/dd/yyyy",
  dateTimeMobile: "MM/dd/yyyy hh:mm aa"
};
const useStyles = makeStyles(theme => ({
  ...textInputStyles(theme),
  datePickerWrapper: {
    background: theme.palette.background.paper,
    overflow: "hidden"
  },
  timePickerWrapper: {}
}));
const DateTimePicker = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    className,
    error,
    errorMessage,
    format,
    placeholder,
    helperText,
    id,
    label,
    pickerType = "date",
    required,
    value,
    variant = "outlined",
    labelProps,
    inputComponent,
    inputProps,
    onChange,
    onAccept,
    executeValidation,
    popoverText,
    customHelperIcon,
    margin,
    minValue,
    maxValue,
    fullWidth,
    localeText,
    clearable = false,
    ...otherProps
  } = props;
  const inputRef = useRef(null);
  const theme = useTheme();
  useEffect(() => {
    if (typeof ref === "function") {
      ref(inputRef.current);
    } else if (ref) {
      ref.current = inputRef.current;
    }
  });
  const handleOnChange = async (date, context) => {
    // If regular date, set time to 00:00:00 so we check the full day in validations.
    if (date && (pickerType === "date" || pickerType === "dateStatic")) {
      date.setHours(0, 0, 0, 0);
    }
    if (executeValidation) {
      await executeValidation(date);
    }
    if (onChange) {
      onChange(date, context);
    }
  };
  const handleOnAccept = async () => {
    if (inputRef && inputRef.current) {
      const defaultFormat = format || DEFAULT_FORMATS[pickerType];
      const typedValue = parseDate(inputRef.current.value, defaultFormat);
      if (typedValue) {
        // If regular date, set time to 00:00:00 so we check the full day in validations.
        if (pickerType === "date" || pickerType === "dateStatic") {
          typedValue.setHours(0, 0, 0, 0);
        }
        if (executeValidation) {
          await executeValidation(typedValue);
        }
        if (onAccept) {
          onAccept(typedValue);
        }
      }
    }
  };
  const pickerProps = {
    value,
    onChange: handleOnChange,
    onAccept: handleOnAccept,
    format,
    label,
    ...otherProps
  };
  const renderedSlots = {
    textField: DateTimeField
  };
  const textField = {
    id,
    "data-testid": props["data-testid"],
    helperText,
    popoverText,
    customHelperIcon,
    error,
    errorMessage,
    fullWidth,
    required,
    // The TextInputs is using a boolean for margin, but the TextField component is using a string
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    margin: margin,
    variant,
    inputComponent,
    inputProps: {
      placeholder,
      ...inputProps
    },
    pickerType,
    labelProps,
    inputRef,
    className
  };
  const clearableProps = clearable ? {
    field: {
      clearable: true
    }
  } : {};
  return /*#__PURE__*/React.createElement(React.Fragment, null, pickerType === "dateTime" && /*#__PURE__*/React.createElement(MUIDateTimePicker, _extends({
    ref: ref
  }, pickerProps, {
    minDateTime: minValue,
    maxDateTime: maxValue,
    slots: {
      ...renderedSlots,
      ...pickerProps.slots,
      toolbar: CustomDateTimePickerToolbar
    },
    slotProps: {
      ...clearableProps,
      textField,
      ...pickerProps.slotProps
    },
    desktopModeMediaQuery: `@media (min-width: ${theme.breakpoints.values.sm}px)`
  })), pickerType === "date" && /*#__PURE__*/React.createElement(DatePicker, _extends({}, pickerProps, {
    minDate: minValue,
    maxDate: maxValue,
    slots: {
      ...renderedSlots,
      ...pickerProps.slots,
      toolbar: CustomDatePickerToolbar
    },
    slotProps: {
      ...clearableProps,
      textField,
      ...pickerProps.slotProps
    },
    desktopModeMediaQuery: `@media (min-width: ${theme.breakpoints.values.sm}px)`
  })), pickerType === "time" && /*#__PURE__*/React.createElement(TimePicker, _extends({}, pickerProps, {
    minTime: minValue,
    maxTime: maxValue,
    slots: {
      ...renderedSlots,
      ...pickerProps.slots,
      toolbar: CustomTimePickerToolbar
    },
    slotProps: {
      ...clearableProps,
      textField,
      ...pickerProps.slotProps
    },
    desktopModeMediaQuery: `@media (min-width: ${theme.breakpoints.values.sm}px)`
  })), pickerType === "dateStatic" && /*#__PURE__*/React.createElement(StaticDatePicker, _extends({}, pickerProps, {
    minDate: minValue,
    maxDate: maxValue
  })), pickerType === "timeStatic" && /*#__PURE__*/React.createElement(StaticTimePicker, _extends({}, pickerProps, {
    minTime: minValue,
    maxTime: maxValue
  })), pickerType === "dateMobile" && /*#__PURE__*/React.createElement(MobileDatePicker, _extends({}, pickerProps, {
    minDate: minValue,
    maxDate: maxValue,
    slots: {
      ...renderedSlots,
      ...pickerProps.slots,
      toolbar: CustomDatePickerToolbar
    },
    slotProps: {
      ...clearableProps,
      textField,
      ...pickerProps.slotProps
    }
  })), pickerType === "dateTimeMobile" && /*#__PURE__*/React.createElement(MobileDateTimePicker, _extends({}, pickerProps, {
    minDateTime: minValue,
    maxDateTime: maxValue,
    slots: {
      ...renderedSlots,
      ...pickerProps.slots,
      toolbar: CustomDateTimePickerToolbar
    },
    slotProps: {
      ...clearableProps,
      textField,
      ...pickerProps.slotProps
    }
  })));
});
DateTimePicker.displayName = "DateTimePicker";
DateTimePicker.defaultProps = {
  pickerType: "date",
  variant: "outlined"
};
export const DateTimeField = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    inputRef,
    pickerType,
    className,
    inputComponent,
    labelProps,
    inputProps,
    InputProps,
    ...otherProps
  } = props;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement(TextInput, _extends({
    ref: ref,
    inputRef: inputRef
  }, otherProps, InputProps, {
    inputProps: {
      ...inputProps,
      placeholder: inputProps?.placeholder || props.placeholder,
      "aria-label": props.label || props["aria-label"]
    },
    InputProps: {
      inputComponent: inputComponent,
      className: classNames("prmbl-textInput-input", classes[props.variant + "Input"]),
      ...InputProps,
      // TODO: Figure it out why MobilePicker types don't show endAdornment as the others
      endAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
        position: "end",
        sx: {
          color: "rgba(0, 0, 0, 0.54)"
        }
      }, /*#__PURE__*/React.createElement(Calendar, null))
    },
    InputLabelProps: {
      className: classNames("prmbl-textInput-label", classes[props.variant + "Label"]),
      classes: {
        shrink: classes[props.variant + "ShrunkLabel"]
      },
      ...labelProps,
      id: `${props.id}-label`
    },
    FormControlProps: {
      className: classNames(`prmbl-${pickerType}Picker`, className, classes[props.variant + "Wrapper"])
    }
  }));
});
DateTimeField.displayName = "DateTimeField";
export const DateTimePickerWrapper = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    locale,
    ...otherProps
  } = props;
  return /*#__PURE__*/React.createElement(LocalizationProvider, {
    dateAdapter: AdapterDateFns,
    adapterLocale: locale
  }, /*#__PURE__*/React.createElement(DateTimePicker, _extends({
    ref: ref
  }, otherProps)));
});
DateTimePickerWrapper.displayName = "DateTimePickerWrapper";
export default DateTimePickerWrapper;